// src/theme.js
import { createTheme } from '@mui/material/styles';
import { themeConfig } from './config/themeConfig';

/**
 * Theme factory function that generates Material-UI theme objects
 * based on the current dark mode state
 * @param {boolean} isDarkMode - Whether dark mode is enabled
 * @returns {object} Material-UI theme object
 */
export const createAppTheme = (isDarkMode = false) => {
  const themeColors = isDarkMode ? themeConfig.dark : themeConfig.light;
  
  return createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: themeColors.primary,
        contrastText: isDarkMode ? '#ffffff' : '#ffffff',
      },
      secondary: {
        main: themeColors.secondary,
        contrastText: themeColors.text,
      },
      background: {
        default: themeColors.background,
        paper: isDarkMode ? '#2a2a2a' : '#ffffff',
      },
      text: {
        primary: themeColors.text,
        secondary: isDarkMode ? '#b0b0b0' : '#666666',
      },
      action: {
        hover: themeColors.buttonHover,
      },
    },
    typography: {
      fontFamily: 'Nunito, Roboto, Helvetica, Arial, sans-serif',
      fontSize: 14, // Base font size set to 14px
      h1: {
        color: themeColors.text,
        fontSize: '2.5rem', // 40px
      },
      h2: {
        color: themeColors.text,
        fontSize: '2rem', // 32px
      },
      h3: {
        color: themeColors.text,
        fontSize: '1.75rem', // 28px
      },
      h4: {
        color: themeColors.text,
        fontSize: '1.5rem', // 24px
      },
      h5: {
        color: themeColors.text,
        fontSize: '1.25rem', // 20px
      },
      h6: {
        color: themeColors.text,
        fontSize: '1.125rem', // 18px
      },
      body1: {
        color: themeColors.text,
        fontSize: '1rem', // 16px (14px base * 1rem = 14px, but we want 16px for body text)
      },
      body2: {
        color: themeColors.text,
        fontSize: '0.875rem', // 14px
      },
      caption: {
        fontSize: '0.875rem', // 14px
      },
      button: {
        fontSize: '1rem', // 16px
      },
      subtitle1: {
        fontSize: '1rem', // 16px
      },
      subtitle2: {
        fontSize: '0.875rem', // 14px
      },
    },
    components: {
      // Customize Material-UI components to work with our theme
      MuiButton: {
        styleOverrides: {
          root: {
            backgroundColor: themeColors.buttonBackground,
            color: themeColors.buttonText,
            '&:hover': {
              backgroundColor: themeColors.buttonHover,
              color: isDarkMode ? '#ffff00' : themeColors.buttonText,
            },
          },
          contained: {
            backgroundColor: themeColors.primaryButtonBackground,
            color: themeColors.primaryButtonText,
            '&:hover': {
              backgroundColor: themeColors.primaryButtonHover,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputLabel-root': {
              color: themeColors.inputLabel,
            },
            '& .MuiOutlinedInput-root': {
              color: themeColors.inputText,
              '& fieldset': {
                borderColor: themeColors.secondary,
              },
              '&:hover fieldset': {
                borderColor: themeColors.primary,
              },
              '&.Mui-focused fieldset': {
                borderColor: themeColors.primary,
              },
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: themeColors.linkColor,
            '&:hover': {
              color: themeColors.linkHover,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: themeColors.navBackground,
            color: themeColors.navText,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
            color: themeColors.text,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: '14px',
            color: isDarkMode ? '#ffff00' : '#87CEEB',
            backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(97, 97, 97, 0.9)',
          },
        },
      },
    },
  });
};

// Default theme (light mode) for backward compatibility
const theme = createAppTheme(false);

export default theme;
